import ReactGA from 'react-ga4';
import { Box, Select, MenuItem, ListSubheader, Stack, SelectChangeEvent, InputLabel, Typography, FormControl } from '@mui/material';
import { useStore } from 'react-admin';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { HistoricalPeriod, historicalPeriodMap } from '@models/historical-period-map';
import { dateToFilterString } from '@models/date-to-filter-string';
import { dateRangeItem, DateRangeMenuItem } from './date-range-menu-item';

export type DateRangePickerProps = {
  defaultFromDate: Dayjs
  defaultToDate: Dayjs
  defaultPeriodId: number;
}

export const DateRangePicker = ({ defaultFromDate, defaultToDate, defaultPeriodId }: DateRangePickerProps) => {
  const minDate = dayjs('2016-01-01');
  const maxDate = defaultToDate.endOf('month');

  const [_, setDateRange] = useStore<string[]>('map.dateRange', [dateToFilterString(defaultFromDate), dateToFilterString(defaultToDate)]);

  const [periodId, setPeriodId] = useState<number>(defaultPeriodId);
  const [toDateValue, setToValue] = useState<Dayjs | undefined>(defaultToDate ?? dayjs());

  const onToChange = (newValue: Dayjs | null) => {
    if (!newValue) {
      throw new Error(`Historical Period TO date not valid.`);
    }
    setToValue(newValue);
    const period = historicalPeriodMap.find((p) => p.id === periodId);
    if (!period) {
      throw new Error(`Period with id '${periodId}' not found.`);
    }
    updateDateRange(period, newValue);

    // Send event to Google Analytics
    ReactGA.event('change_to_date', {
      category: 'DateRangePicker',
      action: 'Change To Date',
      label: period.name,
      value: newValue.format('YYYY-MM-DD'),
    });
  };

  const onPerionChange = (event: SelectChangeEvent<number>) => {
    const newPeriodId = event.target.value as number;
    const period = historicalPeriodMap.find((p) => p.id === newPeriodId);
    if (!period) {
      throw new Error(`Period with id '${newPeriodId}' not found.`);
    }
    setPeriodId(period.id);
    if (!toDateValue) {
      throw new Error(`Historical Period TO date not valid.`);
    }
    updateDateRange(period, toDateValue);

    // Send event to Google Analytics
    ReactGA.event('change_historical_period', {
      category: 'DateRangePicker',
      action: 'Change Historical Period',
      label: period.name,
      value: period.id,
    });
  };

  const updateDateRange = (period: HistoricalPeriod, toDate: Dayjs) => {
    const fromDate = toDate.subtract(period.months, 'month');
    setDateRange([dateToFilterString(fromDate), dateToFilterString(toDate)]);
  }

  return (
    <Box>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <FormControl>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <InputLabel id='date-range-picker'>Historical Period</InputLabel>
            <Select
              defaultValue={defaultPeriodId}
              onChange={onPerionChange}
              sx={{ width: 100, height: '100%' }}
              value={periodId}
              size='medium'
              variant='standard'
            >
              {historicalPeriodMap.map((p) => (
                // <MenuItem value={p.id} key={p.id}>{p.name}</MenuItem>)
                <DateRangeMenuItem key={p.id} period={p} value={p.id}>
                  {dateRangeItem(p)}
                </DateRangeMenuItem>
              ))}
            </Select>
            <Typography variant="h4" sx={{ margin: 1 }} >{'-'}</Typography>
            <DatePicker
              minDate={minDate}
              maxDate={maxDate}
              onChange={(newValue) => onToChange(newValue)}
              slotProps={{ textField: { variant: 'standard', size: 'medium', margin: 'normal' } }}
              sx={{
                width: 160,
              }}
              value={toDateValue}
              views={['month', 'year']}
            />
          </Box>
        </FormControl>
      </LocalizationProvider>
    </Box>
  );
};
